import { forwardRef, HTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import "./gradient-border-box.css";

export const RadiusStyles = {
  circle:
    "before:rounded-[50%] rounded-[50%] before:p-[0.0625rem] p-[0.0625rem]",
  bigger:
    "before:rounded-[1.5000rem] before:p-[0.0625rem] rounded-[1.5000rem] p-[0.0625rem]",
  medium:
    "before:rounded-[0.625rem] before:p-[0.0625rem] rounded-[0.625rem] p-[0.0625rem]",
  normal:
    "before:rounded-[0.3125rem] before:p-[0.0625rem] rounded-[0.3125rem] p-[0.0625rem]",
  smaller:
    "before:rounded-[0.1875rem] before:p-[0.0625rem] rounded-[0.1875rem] p-[0.0625rem]",
  left: "before:pl-[0.0625rem] m-[-0.0628rem]",
  right: "before:pr-[0.0625rem] m-[-0.0628rem]",
  smallInput:
    "before:rounded-[3.125rem] rounded-[3.125rem] before:p-[0.0625rem] p-[0.0625rem]",
  none: "before:rounded-[0] rounded-[0%] before:p-[0.0625rem] p-[0.0625rem]",
};

export const GradientStyles = {
  "50": "before:bg-gradient-50",
  "100": "before:bg-gradient-100",
  "reversed-100": "before:bg-gradient-100-reversed",
  "input-50": "before:bg-[#717BF7] before:bg-opacity-50",
  "input-50-min": "before:bg-[#5FF0E0] before:bg-opacity-50",
  "default-border": "before:bg-border2",
  green2: "before:bg-green2",
  red2: "before:bg-red2",
  blue: "before:bg-blue",
  none: "before:bg-transparent",
};

export const HoverStyles = {
  "50": "hover:bg-gradient-50 cursor-pointer",
  "100": "hover:bg-gradient-100 cursor-pointer",
  "r-100": "before:hover:bg-gradient-100 cursor-pointer",
  "r-50": "before:hover:bg-gradient-50 cursor-pointer",
  "input-50":
    "before:hover:bg-[#717BF7] before:bg-opacity-50 group-hover:before:bg-[#717BF7] group-focus-within:before:bg-[#717BF7]",
  "input-50-right":
    "before:hover:bg-[#717BF7] before:bg-opacity-50 group-hover:before:bg-[#717BF7] group-focus-within:before:bg-[#717BF7]",
  "input-50-left":
    "before:hover:bg-[#5FF0E0] before:bg-opacity-50 group-hover:before:bg-[#5FF0E0] group-focus-within:before:bg-[#5FF0E0]",
  none: "",
};

export const ActiveStyles = {
  default:
    "before:bg-gradient-50 bg-transparent before:hover:bg-gradient-50 cursor-pointer",
  "50": "bg-gradient-50 cursor-pointer",
  "100": "bg-gradient-100 cursor-pointer",
  "b-50":
    "before:bg-gradient-100 bg-switch-button cursor-pointer before:hover:bg-gradient-100 cursor-pointer",
  "b-100": "before:bg-gradient-100 bg-gradient-100 cursor-pointer",
  "r-50": "before:bg-gradient-50 cursor-pointer",
  "r-100": "before:bg-gradient-100 cursor-pointer",
  "input-50":
    "before:bg-[#717BF7] before:bg-opacity-50 group-hover:before:bg-[#717BF7]",
  "input-50-right":
    "before:bg-[#717BF7] before:bg-opacity-50 group-hover:before:bg-[#717BF7]",
  "input-50-left":
    "before:bg-[#5FF0E0] before:bg-opacity-50 group-hover:before:bg-[#5FF0E0]",
  "sing-in-button": "bg-sing-in-button before:bg-gradient-50  cursor-pointer",
  green2: "before:bg-green2 cursor-pointer",
  red2: "before:bg-red2 cursor-pointer",
  blue: "before:bg-blue cursor-pointer",
  none: "",
};

export const BackgroundStyles = {
  default: "bg-transparent",
  black: "bg-black",
  "section-gradient": "bg-black bg-section-gradient",
  "filter-background": "bg-filter-background",
  "50": "bg-gradient-50",
  "100": "bg-gradient-100",
  "switch-button": "bg-switch-button",
  "input-50": "bg-[#717BF7] bg-opacity-50",
  "input-50-left": "bg-[#5FF0E0] bg-opacity-50",
  green2Bg: "bg-green2Bg",
  red2Bg: "bg-red2Bg",
  blueBg: "bg-blueBg",
};

interface GradientBorderRadiusProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  radius?: keyof typeof RadiusStyles;
  gradient?: keyof typeof GradientStyles;
  background?: keyof typeof BackgroundStyles;
  hover?: keyof typeof HoverStyles;
  active?: keyof typeof ActiveStyles;
  overflow?: boolean;
  className?: string;
  hoverStyle?: keyof typeof HoverStyles;
}

const GradientBorderRadius = forwardRef<
  HTMLDivElement,
  GradientBorderRadiusProps
>(
  (
    {
      children,
      radius = "normal",
      gradient = "100",
      background = "default",
      hover = null,
      active = null,
      overflow = false,
      className = null,
      ...rest
    },
    ref
  ) => {
    const resolvedVariant = radius && RadiusStyles[radius];
    const resolvedGradient = gradient && GradientStyles[gradient];
    const resolvedBackground = background && BackgroundStyles[background];
    const resolvedHover = hover && HoverStyles[hover];
    const resolvedActive = active && ActiveStyles[active];

    if (className?.includes("p-")) {
      console.error(
        "[Gradient-border-radius] Padding is not allowed as it is being used to shift content inside for the pseudo before element. Avoid this error by adding first child element div with padding that you want.",
        className
      );
    }
    return (
      <div
        ref={ref}
        {...rest}
        className={clsx(
          "gradient-box relative",
          resolvedActive,
          resolvedVariant,
          resolvedGradient,
          resolvedBackground,
          resolvedHover,
          overflow && "overflow-hidden before:z-10",
          className
        )}
      >
        {children}
      </div>
    );
  }
);

GradientBorderRadius.displayName = "GradientBorderRadius";

export default GradientBorderRadius;
